/* Checks switched off for one position rather than for the whole book.
 *
 * `checksOff` already lets a rule be turned off everywhere, and that is the
 * right shape for "I do not run this rule". It is the wrong shape for the thing
 * people actually want, which is narrower: this rule is a fair point about
 * eleven of my positions and not about the twelfth. Turning it off everywhere
 * to quiet one holding trades a card you did not want for eleven you did.
 *
 * So: per position, per check. `mutedMovers` already does exactly this for the
 * price-move alerts, and this follows it rather than inventing a second shape —
 * a flat list of strings in settings, cheap to store, cheap to migrate, and
 * readable in an export.
 *
 * ── what silencing does and does not do ──────────────────────────────────
 *
 * It stops the card being drawn. It does not mark the breach resolved, correct
 * anything, or change a number: the position is still over its ceiling and
 * every figure on the screen still says so. The book states facts and does not
 * decide which of them matter (§1) — that judgement is the user's, and this is
 * where they record it.
 *
 * A silenced check leaves no trace in the alert log either. That was a choice
 * with a real argument on both sides: keeping the episode would mean the record
 * still answers "was this ever wrong" a month later. Against it, and what
 * settled it, is that an episode nobody is being shown is a note the book is
 * keeping about a person behind their back. Saying "this is not a problem" and
 * having the book write it down anyway is the wrong relationship. Episodes
 * already open when a check is silenced are closed rather than deleted — the
 * card stops, the history of when it ran does not get rewritten.
 *
 * ── the one that is not a position alert ─────────────────────────────────
 *
 * `noThesis` is a single card for the whole book listing every undocumented
 * ticker, so it carries no `positionId` and cannot be dropped by id the way the
 * others can. Silencing it for one ticker takes that ticker out of the list,
 * and the card disappears on its own once the list is empty. The same key does
 * double duty for the NO THESIS badge on the position's own screen, which is
 * drawn from the thesis record rather than from any alert — two places, one
 * decision, which is the point of putting the decision in its own file. */

/** A check silenced on one position. Ticker and kind, colon-separated: no
    ticker contains a colon, and the kinds are single words by the rule the
    engine already keeps for its alert ids. */
export function silenceKey(ticker: string, kind: string): string {
  return `${ticker.trim().toUpperCase()}:${kind}`;
}

/** Whether this check has been silenced on this position. */
export function isSilenced(
  silenced: string[] | undefined,
  ticker: string,
  kind: string,
): boolean {
  return silenced?.includes(silenceKey(ticker, kind)) ?? false;
}

/** The list with this one added. Idempotent, so pressing it twice is pressing
    it once. */
export function silence(
  silenced: string[] | undefined,
  ticker: string,
  kind: string,
): string[] {
  const key = silenceKey(ticker, kind);
  const current = silenced ?? [];
  return current.includes(key) ? current : [...current, key];
}

/** The list with this one taken out. */
export function unsilence(
  silenced: string[] | undefined,
  ticker: string,
  kind: string,
): string[] {
  const key = silenceKey(ticker, kind);
  return (silenced ?? []).filter((s) => s !== key);
}

export interface SilencedEntry {
  ticker: string;
  kind: string;
}

/** Everything silenced, for the settings screen that has to be able to undo it.
    A list nobody can see is a setting nobody can change. Malformed entries are
    dropped rather than shown, since a row with no undo is worse than no row. */
export function silencedEntries(silenced: string[] | undefined): SilencedEntry[] {
  const out: SilencedEntry[] = [];
  for (const raw of silenced ?? []) {
    const cut = raw.indexOf(':');
    if (cut <= 0 || cut === raw.length - 1) continue;
    out.push({ ticker: raw.slice(0, cut), kind: raw.slice(cut + 1) });
  }
  return out.sort((a, b) => a.ticker.localeCompare(b.ticker) || a.kind.localeCompare(b.kind));
}
